
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { IFacilityShift, IFacilityShiftCreate, IWorkerSuggestionsResponseData } from './types';
import { ICancelShiftResponse, IForceCancelShiftResponse } from '../shift/types';
import apiCall from 'utils/api';

export const createFacilityShift = createAsyncThunk(
  'facilityShifts/createFacilityShift',
  async (shiftData: IFacilityShiftCreate, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityShift; message?: string }>('post', '/api/shifts/create/', shiftData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to create facility shift');
    }
  }
);

export const createPastFacilityShift = createAsyncThunk(
  'facilityShifts/createPastFacilityShift',
  async (shiftData: IFacilityShiftCreate, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityShift; message?: string }>('post', '/api/shifts/create-past/', shiftData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to create past facility shift');
    }
  }
);

export const updateFacilityShift = createAsyncThunk(
  'facilityShifts/updateFacilityShift',
  async ({ id, data }: { id: number; data: IFacilityShiftCreate }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityShift; message?: string }>('put', `/api/shifts/update/${id}/`, data);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update facility shift');
    }
  }
);

export const updatePastFacilityShift = createAsyncThunk(
  'facilityShifts/updatePastFacilityShift',
  async ({ id, data }: { id: number; data: IFacilityShiftCreate }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityShift; message?: string }>('put', `/api/shifts/update-past/${id}/`, data);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update past facility shift');
    }
  }
);

export const fetchShiftDetails = createAsyncThunk(
  'facilityShifts/fetchShiftDetails',
  async (shiftId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IFacilityShift }>('get', `/api/shifts/details/${shiftId}/`);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch shift details');
    }
  }
);

export const fetchWorkerSuggestions = createAsyncThunk(
  'facilityShifts/fetchWorkerSuggestions',
  async (shiftId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IWorkerSuggestionsResponseData }>('get', `/worker/shifts/suggestions/${shiftId}/`);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker suggestions');
    }
  }
);

export const assignWorkers = createAsyncThunk(
  'facilityShifts/assignWorkers',
  async (data: { shift_id: number; worker_ids: number[] }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data?: any; message?: string; assigned_workers?: number[]; errors?: any[] }>('post', '/api/shifts/assign-workers/', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to assign workers');
    }
  }
);

export const resetAssignWorkerState = createAction('facilityShifts/resetAssignWorkerState');
export const resetFacilityShiftCreateState = createAction('facilityShifts/resetFacilityShiftCreateState');
export const resetFacilityShiftUpdateState = createAction('facilityShifts/resetFacilityShiftUpdateState');
export const resetFacilityShiftPastCreateState = createAction('facilityShifts/resetFacilityShiftPastCreateState');
export const resetFacilityShiftPastUpdateState = createAction('facilityShifts/resetFacilityShiftPastUpdateState');
export const resetShiftExtensionState = createAction('facilityShifts/resetShiftExtensionState');

export const rebroadcastShift = createAsyncThunk(
  'facilityShifts/rebroadcastShift',
  async (shiftId: number | string, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ status: boolean; message: string }>('post', `/api/shifts/rebroadcast/${shiftId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to re-broadcast shift');
    }
  }
);

export const cancelFacilityShift = createAsyncThunk(
  'facilityShifts/cancelFacilityShift',
  async (
    { shiftId, reason, additionalReason, is_free_cancellation }: { shiftId: number; reason: string; additionalReason?: string; is_free_cancellation?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<ICancelShiftResponse>(
        'patch',
        `/api/shifts/cancel/${shiftId}/`,
        {
          cancellation_reason: reason,
          additional_cancel_reason: additionalReason,
          is_free_cancellation,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel shift');
    }
  },
);

export const forceCancelFacilityShift = createAsyncThunk(
  'facilityShifts/forceCancelFacilityShift',
  async (
    { shiftId, cancellation_reason }: { shiftId: number; cancellation_reason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IForceCancelShiftResponse>(
        'post',
        '/api/organization/shifts/force-cancel/',
        {
          shift_id: shiftId,
          cancellation_reason: cancellation_reason,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to force cancel shift');
    }
  },
);

export const replaceShiftWorker = createAsyncThunk(
  'facilityShifts/replaceShiftWorker',
  async (
    { shiftId, currentWorkerId, newWorkerId, reason }: { shiftId: number; currentWorkerId: number; newWorkerId: number; reason: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<{ message: string; new_assignment_id: number }>(
        'post',
        '/api/shifts/replace-worker/',
        {
          shift_id: shiftId,
          current_worker_id: currentWorkerId,
          new_worker_id: newWorkerId,
          reason: reason,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to replace worker');
    }
  },
);


export const validateExtendShiftTime = createAsyncThunk(
  'facilityShifts/validateExtendShiftTime',
  async (
    { shiftId, shiftTime }: { shiftId: number; shiftTime: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<{ valid: boolean; message: string; conflicts?: any[] }>(
        'post',
        '/api/organization/shifts/validate-extend-time/',
        {
          shift_id: shiftId,
          shift_time: shiftTime,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.conflicts) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to validate shift extension');
    }
  },
);

export const extendShiftTime = createAsyncThunk(
  'facilityShifts/extendShiftTime',
  async (
    { shiftId, facilityId, shiftTime, force }: { shiftId: number; facilityId: number; shiftTime: string; force?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<{ message: string }>(
        'post',
        '/api/organization/shifts/extend-time/',
        {
          shift_id: shiftId,
          facility_id: facilityId,
          shift_time: shiftTime,
          force: force
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.conflicts) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to extend shift time');
    }
  },
);

export const deleteCancelledShift = createAsyncThunk(
  'facilityShifts/deleteCancelledShift',
  async (shiftId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>(
        'delete',
        `/api/organization/shifts/delete-cancelled/${shiftId}/`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete cancelled shift');
    }
  }
);

export const resetDeleteCancelledShiftState = createAction('facilityShifts/resetDeleteCancelledShiftState');

